import { Link } from "react-router-dom";
import Navbar from "../../components/feature/Navbar";
import Footer from "../../components/feature/Footer";
import SEOMeta from "../../components/base/SEOMeta";

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://example.com';

const aboutJsonLd = [
  {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    "name": "About QuizVibe – Fun Personality Quizzes for Kids & Teens",
    "description": "Learn about QuizVibe, the viral personality quiz destination for kids and teens. Our story, team, values, and mission to make the internet more fun.",
    "url": `${SITE_URL}/about`,
    "inLanguage": "en",
    "breadcrumb": {
      "@type": "BreadcrumbList",
      "itemListElement": [
        { "@type": "ListItem", "position": 1, "name": "Home", "item": SITE_URL },
        { "@type": "ListItem", "position": 2, "name": "About", "item": `${SITE_URL}/about` }
      ]
    }
  },
  {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "QuizVibe",
    "url": SITE_URL,
    "description": "QuizVibe is the viral personality quiz destination for kids and teens. Over 500 quizzes covering Disney, Roblox, Anime, and more.",
    "foundingDate": "2021",
    "numberOfEmployees": { "@type": "QuantitativeValue", "value": 4 },
    "sameAs": [],
    "contactPoint": {
      "@type": "ContactPoint",
      "contactType": "Customer Support",
      "email": "privacy@funstest.com"
    }
  }
];

const stats = [
  { num: "5M+", label: "Monthly Players", icon: "ri-user-heart-line" },
  { num: "500+", label: "Quizzes Published", icon: "ri-gamepad-line" },
  { num: "120+", label: "Countries Reached", icon: "ri-earth-line" },
  { num: "4.9★", label: "Average Rating", icon: "ri-star-smile-line" },
];

const values = [
  {
    icon: "ri-heart-pulse-line",
    title: "Fun First",
    color: "from-[#FF6B6B] to-[#FF8E53]",
    desc: "Every quiz we create is designed to spark joy, laughter, and genuine connection. No boring stuff — just pure, feel-good fun.",
  },
  {
    icon: "ri-shield-user-line",
    title: "Safe for Everyone",
    color: "from-[#6EE7B7] to-[#3B82F6]",
    desc: "We build with teens and kids in mind. Our content is reviewed, age-appropriate, and always positive — zero negativity in results.",
  },
  {
    icon: "ri-share-circle-line",
    title: "Made to Share",
    color: "from-[#FBBF24] to-[#F59E0B]",
    desc: "The best quiz is the one you send to your BFF at 2am. We craft every result to be screenshot-worthy and endlessly shareable.",
  },
  {
    icon: "ri-lightbulb-flash-line",
    title: "Creator Powered",
    color: "from-[#A78BFA] to-[#EC4899]",
    desc: "Our quiz library is built by passionate creators worldwide. We love collaborating with writers who understand what teens really love.",
  },
];

const team = [
  {
    name: "Mia Hartwell",
    role: "Founder & Chief Quiz Officer",
    bio: "Obsessed with pop culture since age 10. Started FunsTest from a college dorm after going viral with a 'Which Disney Princess Are You?' quiz.",
    img: "https://public.readdy.ai/ai/img_res/8dd5683108651cb88a40d38fc997e695.jpg",
  },
  {
    name: "Jake Prentiss",
    role: "Head of Content & Trends",
    bio: "Spends 6 hours a day on TikTok for work — and somehow that's a real job. Jake spots the next viral trend before it even peaks.",
    img: "https://readdy.ai/api/search-image?query=young%20cheerful%20male%20content%20creator%20professional%20portrait%20smile%20casual%20streetwear%20creative%20studio%20background%20natural%20light%20photography%20millennial&width=400&height=400&seq=team002&orientation=squarish",
  },
  {
    name: "Zoe Nakamura",
    role: "Lead Quiz Designer",
    bio: "Former game designer turned quiz wizard. Zoe's the one who makes sure every question feels like a choose-your-own-adventure.",
    img: "https://readdy.ai/api/search-image?query=young%20asian%20female%20designer%20professional%20portrait%20creative%20happy%20warm%20background%20studio%20light%20natural%20casual%20trendy%20style%20photography&width=400&height=400&seq=team003&orientation=squarish",
  },
  {
    name: "Carlos Rivera",
    role: "Community & Creator Lead",
    bio: "Runs our creator program and loves discovering new voices. If you want to write for us, Carlos is your guy.",
    img: "https://readdy.ai/api/search-image?query=young%20latino%20male%20community%20manager%20professional%20portrait%20smile%20casual%20office%20creative%20background%20warm%20natural%20light%20photography&width=400&height=400&seq=team004&orientation=squarish",
  },
];

const milestones = [
  { year: "2021", event: "FunsTest launched with just 12 quizzes and a dream" },
  { year: "2022", event: "Hit 500K monthly users after Toca Boca quiz went viral on TikTok" },
  { year: "2023", event: "Opened the Creator Program — 200+ writers joined in year one" },
  { year: "2024", event: "Reached 3M monthly players, expanded to 120 countries" },
  { year: "2025", event: "Launched image-choice quizzes, engagement tripled overnight" },
  { year: "2026", event: "5M+ players, 500+ quizzes — and we\u2019re just getting started!" },
];

export default function AboutPage() {
  return (
    <div className="min-h-screen bg-white">
      <SEOMeta
        title="About QuizVibe – Our Story, Team & Mission | Fun Quizzes for Kids & Teens"
        description="Learn about QuizVibe, the home of viral personality quizzes for kids and teens. Meet the team behind 500+ quizzes enjoyed by 5M+ monthly players worldwide."
        keywords="about QuizVibe, quiz website, personality quiz team, fun quizzes for kids, teens quiz creator"
        canonicalPath="/about"
        ogImage="https://readdy.ai/api/search-image?query=quizvibe%20fun%20viral%20personality%20quiz%20website%20kids%20teens%20colorful%20logo%20banner%20bright%20cheerful%20cartoon&width=1200&height=630&seq=og001&orientation=landscape"
        jsonLd={aboutJsonLd}
      />
      <Navbar />

      {/* Hero */}
      <section className="relative pt-32 pb-24 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=colorful%20fun%20quiz%20game%20abstract%20background%20confetti%20stars%20bokeh%20kawaii%20playful%20art%20bright%20warm%20coral%20orange%20pink%20teal%20soft%20gradient%20illustration%20minimal&width=1440&height=700&seq=about-hero&orientation=landscape"
            alt="About FunsTest"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-[#FF6B6B]/85 via-[#FF8E53]/75 to-[#FBBF24]/65" />
        </div>
        <div className="relative z-10 max-w-4xl mx-auto px-6 text-center">
          <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm border border-white/30 rounded-full px-4 py-1.5 mb-6">
            <i className="ri-gamepad-fill text-white" />
            <span className="text-white text-sm font-semibold">Our Story</span>
          </div>
          <h1
            className="text-5xl md:text-6xl font-extrabold text-white leading-tight mb-6"
            style={{ fontFamily: "'Nunito', sans-serif" }}
          >
            We Make the Internet<br />
            <span className="text-yellow-300">More Fun</span>
          </h1>
          <p className="text-white/90 text-lg md:text-xl max-w-2xl mx-auto leading-relaxed">
            FunsTest is where millions of teens come to discover themselves, laugh with friends, and share results that say everything about who they really are.
          </p>
        </div>
      </section>

      {/* Stats */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((s) => (
              <div key={s.label} className="text-center">
                <div className="w-14 h-14 flex items-center justify-center mx-auto mb-4 rounded-2xl bg-gradient-to-br from-[#FF6B6B]/10 to-[#FBBF24]/10">
                  <i className={`${s.icon} text-2xl text-[#FF6B6B]`} />
                </div>
                <p
                  className="text-4xl font-extrabold text-gray-900 mb-1"
                  style={{ fontFamily: "'Nunito', sans-serif" }}
                >
                  {s.num}
                </p>
                <p className="text-sm text-gray-500 font-medium">{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Mission */}
      <section className="py-20 bg-[#FFF8F6]">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-[#FF6B6B] font-bold text-sm uppercase tracking-wider mb-3">Our Mission</p>
              <h2
                className="text-4xl font-extrabold text-gray-900 mb-6 leading-tight"
                style={{ fontFamily: "'Nunito', sans-serif" }}
              >
                Every quiz should feel like a moment with your best friend
              </h2>
              <p className="text-gray-500 text-base leading-relaxed mb-6">
                We started FunsTest because the internet needed more joy. Not more news, not more drama — just pure, wholesome, shareable fun that brings people together.
              </p>
              <p className="text-gray-500 text-base leading-relaxed mb-8">
                Today, we're the go-to destination for teens who want to explore their personality, connect with fandoms they love, and create memories worth sharing. Every result you get on FunsTest is crafted to make you smile and say "omg this is SO me."
              </p>
              <Link
                to="/categories"
                className="inline-flex items-center gap-2 px-6 py-3 bg-[#FF6B6B] text-white font-bold rounded-full hover:bg-[#ff5252] transition-colors cursor-pointer whitespace-nowrap"
              >
                Explore Our Quizzes
                <i className="ri-arrow-right-line" />
              </Link>
            </div>
            <div className="relative">
              <div className="rounded-3xl overflow-hidden w-full h-80">
                <img
                  src="https://readdy.ai/api/search-image?query=group%20of%20happy%20diverse%20teenagers%20laughing%20together%20looking%20at%20phone%20quiz%20results%20sharing%20social%20media%20fun%20candid%20lifestyle%20photography%20warm%20sunlight%20natural%20colors&width=600&height=400&seq=about-mission&orientation=landscape"
                  alt="FunsTest Community"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl p-4 w-44">
                <p className="text-xs text-gray-400 mb-1">This week's top quiz</p>
                <p className="text-sm font-bold text-gray-800 leading-snug">"Which Disney Princess Are You?"</p>
                <p className="text-xs text-[#FF6B6B] font-semibold mt-1">2.1M plays</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-14">
            <p className="text-[#FF6B6B] font-bold text-sm uppercase tracking-wider mb-2">What We Stand For</p>
            <h2
              className="text-4xl font-extrabold text-gray-900"
              style={{ fontFamily: "'Nunito', sans-serif" }}
            >
              Our Core Values
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((v) => (
              <div key={v.title} className="bg-[#FFF8F6] rounded-2xl p-6 border border-orange-50">
                <div className={`w-12 h-12 flex items-center justify-center rounded-xl bg-gradient-to-br ${v.color} mb-4`}>
                  <i className={`${v.icon} text-white text-xl`} />
                </div>
                <h3
                  className="font-extrabold text-gray-900 text-base mb-2"
                  style={{ fontFamily: "'Nunito', sans-serif" }}
                >
                  {v.title}
                </h3>
                <p className="text-gray-500 text-sm leading-relaxed">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-20 bg-[#FFF8F6]">
        <div className="max-w-3xl mx-auto px-6">
          <div className="text-center mb-14">
            <p className="text-[#FF6B6B] font-bold text-sm uppercase tracking-wider mb-2">How We Got Here</p>
            <h2
              className="text-4xl font-extrabold text-gray-900"
              style={{ fontFamily: "'Nunito', sans-serif" }}
            >
              Our Journey
            </h2>
          </div>
          <div className="relative">
            <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-gradient-to-b from-[#FF6B6B] to-[#FBBF24]" />
            <div className="space-y-8">
              {milestones.map((m, i) => (
                <div key={m.year} className="flex gap-6 items-start pl-16 relative">
                  <div className="absolute left-0 w-12 h-12 flex items-center justify-center rounded-full bg-gradient-to-br from-[#FF6B6B] to-[#FF8E53] text-white text-xs font-extrabold flex-shrink-0"
                    style={{ fontFamily: "'Nunito', sans-serif" }}
                  >
                    {m.year.slice(2)}
                  </div>
                  <div className={`bg-white rounded-2xl p-4 w-full border ${i === milestones.length - 1 ? "border-[#FF6B6B]/30" : "border-gray-100"}`}>
                    <p className="text-[#FF6B6B] font-bold text-xs mb-1">{m.year}</p>
                    <p className="text-gray-700 text-sm leading-relaxed">{m.event}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Team */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-14">
            <p className="text-[#FF6B6B] font-bold text-sm uppercase tracking-wider mb-2">The People Behind the Fun</p>
            <h2
              className="text-4xl font-extrabold text-gray-900"
              style={{ fontFamily: "'Nunito', sans-serif" }}
            >
              Meet the Team
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {team.map((member) => (
              <div key={member.name} className="group text-center">
                <div className="w-28 h-28 mx-auto rounded-full overflow-hidden mb-4 ring-4 ring-[#FF6B6B]/10 group-hover:ring-[#FF6B6B]/40 transition-all">
                  <img
                    src={member.img}
                    alt={member.name}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <h4 className="font-extrabold text-gray-900 text-sm" style={{ fontFamily: "'Nunito', sans-serif" }}>
                  {member.name}
                </h4>
                <p className="text-[#FF6B6B] text-xs font-semibold mt-0.5 mb-2">{member.role}</p>
                <p className="text-gray-500 text-xs leading-relaxed">{member.bio}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-br from-[#FF6B6B] to-[#FF8E53]">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h2
            className="text-4xl font-extrabold text-white mb-4"
            style={{ fontFamily: "'Nunito', sans-serif" }}
          >
            Want to be part of FunsTest?
          </h2>
          <p className="text-white/80 text-base mb-8 leading-relaxed">
            We're always looking for passionate creators to grow our quiz library. If you love pop culture, games, and making teens smile — we want to hear from you!
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link
              to="/write-for-us"
              className="px-8 py-3.5 bg-white text-[#FF6B6B] font-extrabold rounded-full hover:scale-105 transition-transform cursor-pointer whitespace-nowrap"
            >
              Write for Us ✍️
            </Link>
            <Link
              to="/categories"
              className="px-8 py-3.5 bg-white/20 border-2 border-white text-white font-bold rounded-full hover:bg-white/30 transition-colors cursor-pointer whitespace-nowrap"
            >
              Take a Quiz First
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
